import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserInviteProgressSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function UserInviteProgressSubTab() {
  const [progressList, setProgressList] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProgress, setEditingProgress] = useState(null);
  const [formData, setFormData] = useState({ user_id: '', step: '1' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [progressRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/user-invite-progress`),
        axios.get(`${API_URL}/api/users`)
      ]);
      setProgressList(progressRes.data);
      setUsers(usersRes.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке данных: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        user_id: parseInt(formData.user_id),
        step: parseInt(formData.step)
      };
      
      if (editingProgress) {
        await axios.put(`${API_URL}/api/user-invite-progress/${editingProgress.id}`, { step: payload.step });
        setEditingProgress(null);
      } else {
        await axios.post(`${API_URL}/api/user-invite-progress`, payload);
      }
      setShowForm(false);
      setFormData({ user_id: '', step: '1' });
      fetchData();
    } catch (err) {
      setError('Ошибка при сохранении прогресса: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту запись прогресса?')) {
      try {
        await axios.delete(`${API_URL}/api/user-invite-progress/${id}`);
        fetchData();
      } catch (err) {
        setError('Ошибка при удалении прогресса: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProgress(null);
    setFormData({ user_id: '', step: '1' });
  };

  const handleEdit = (progress) => {
    setEditingProgress(progress);
    setFormData({
      user_id: progress.user_id.toString(),
      step: progress.step.toString()
    });
    setShowForm(true);
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `ID: ${user.telegram_id}` : `ID: ${userId}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const getStepLabel = (step) => {
    const labels = {
      1: 'Шаг 1: Открыл ITinterview',
      2: 'Шаг 2: Отправил 3 ответа',
      3: 'Шаг 3: Подписался'
    };
    return labels[step] || `Шаг ${step}`;
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="user-invite-progress-sub-tab">
      <div className="tab-header">
        <h2>Прогресс приглашений пользователей</h2>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить прогресс
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container">
          <h3>{editingProgress ? 'Редактировать прогресс' : 'Добавить новый прогресс'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="user_id">Пользователь *</label>
              <select
                id="user_id"
                value={formData.user_id}
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                required
                disabled={!!editingProgress}
              >
                <option value="">Выберите пользователя</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    Telegram ID: {user.telegram_id} {user.topic_title ? `(${user.topic_title})` : ''}
                  </option>
                ))}
              </select>
              {editingProgress && (
                <small className="form-hint">
                  Пользователь не может быть изменен при редактировании
                </small>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="step">Шаг прогресса *</label>
              <select
                id="step"
                value={formData.step}
                onChange={(e) => setFormData({ ...formData, step: e.target.value })}
                required
              >
                <option value="1">Шаг 1: Открыл ITinterview</option>
                <option value="2">Шаг 2: Отправил 3 ответа</option>
                <option value="3">Шаг 3: Подписался</option>
              </select>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingProgress ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Пользователь</th>
              <th>Telegram ID</th>
              <th>Шаг</th>
              <th>Создано</th>
              <th>Обновлено</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {progressList.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  Нет записей прогресса. Добавьте первую запись.
                </td>
              </tr>
            ) : (
              progressList.map((progress) => (
                <tr key={progress.id}>
                  <td>{progress.id}</td>
                  <td>{getUserName(progress.user_id)}</td>
                  <td>{progress.user_telegram_id || '-'}</td>
                  <td>
                    <span className={`step-badge step-${progress.step}`}>
                      {getStepLabel(progress.step)}
                    </span>
                  </td>
                  <td>{formatDate(progress.created_at)}</td>
                  <td>{formatDate(progress.updated_at)}</td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(progress)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(progress.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UserInviteProgressSubTab;
